import { Component, computed, inject } from '@angular/core';
import { ExportJob, ExportJobsService } from '@shared/export/export-jobs.service';
import { AppConfirmDialogComponent } from '@shared/components/app-confirm-dialog/app-confirm-dialog.component';

/**
 * Dock global de exportaciones: lista los trabajos con su progreso, sin bloquear la pantalla.
 * Se monta una sola vez en el layout del panel.
 */
@Component({
  selector: 'app-export-dock',
  imports: [AppConfirmDialogComponent],
  template: `
    @if (exports.jobs().length > 0) {
      <section aria-label="Exportaciones" class="fixed bottom-4 right-4 z-40 w-80 max-w-[calc(100vw-2rem)] bg-white border border-slate-200 rounded-2xl shadow-md overflow-hidden">
        <header class="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <span class="text-sm font-medium text-slate-900">Exportaciones</span>
          <span class="text-2xs text-slate-500">{{ activeCount() }} en curso</span>
        </header>
        <ul class="max-h-80 overflow-y-auto divide-y divide-slate-100">
          @for (job of exports.jobs(); track job.id) {
            <li class="px-4 py-3 flex flex-col gap-2">
              <div class="flex items-start justify-between gap-2">
                <div class="min-w-0">
                  <p class="text-sm text-slate-900 truncate">{{ job.title }}</p>
                  <p class="text-xs" [class.text-slate-500]="job.status !== 'error'" [class.text-danger-600]="job.status === 'error'">{{ statusText(job) }}</p>
                </div>
                <div class="flex items-center gap-1 shrink-0">
                  @if (job.status === 'done') {
                    <button type="button" class="p-1.5 rounded-lg text-slate-600 hover:bg-slate-100" aria-label="Descargar otra vez" (click)="exports.redownload(job.id)">
                      <i class="pi pi-download text-sm" aria-hidden="true"></i>
                    </button>
                  }
                  @if (isActive(job)) {
                    <button type="button" class="p-1.5 rounded-lg text-slate-600 hover:bg-slate-100" aria-label="Cancelar exportación" (click)="exports.cancel(job.id)">
                      <i class="pi pi-times text-sm" aria-hidden="true"></i>
                    </button>
                  } @else {
                    <button type="button" class="p-1.5 rounded-lg text-slate-600 hover:bg-slate-100" aria-label="Quitar de la lista" (click)="exports.dismiss(job.id)">
                      <i class="pi pi-times text-sm" aria-hidden="true"></i>
                    </button>
                  }
                </div>
              </div>
              @if (job.status === 'running') {
                <div class="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden" role="progressbar" [attr.aria-valuenow]="percent(job)" aria-valuemin="0" aria-valuemax="100">
                  <div class="h-full bg-primary-500 transition-[width] duration-200" [style.width.%]="percent(job)"></div>
                </div>
              }
            </li>
          }
        </ul>
      </section>
    }

    <app-confirm-dialog
      [visible]="exports.pending() !== null"
      (visibleChange)="$event || exports.cancelPending()"
      [title]="'Exportar ' + (exports.pending()?.rows ?? 0).toLocaleString('es-EC') + ' registros'"
      description="Se arma un archivo de Excel con los filtros actuales. Va en cola: una exportación grande a la vez."
      icon="pi pi-file-excel"
      confirmLabel="Exportar"
      confirmType="primary"
      (onConfirm)="exports.confirmPending()"
      (onCancel)="exports.cancelPending()" />
  `,
})
export class AppExportDockComponent {
  readonly exports = inject(ExportJobsService);

  readonly activeCount = computed(() => this.exports.jobs().filter((job) => job.status === 'queued' || job.status === 'running').length);

  isActive(job: ExportJob): boolean {
    return job.status === 'queued' || job.status === 'running';
  }

  percent(job: ExportJob): number {
    if (job.expected <= 0) return 0;
    return Math.min(100, Math.round((job.processed / job.expected) * 100));
  }

  statusText(job: ExportJob): string {
    switch (job.status) {
      case 'queued':
        return 'En cola';
      case 'running':
        return `Exportando ${this.percent(job)}% · ${job.processed.toLocaleString('es-EC')} de ${job.expected.toLocaleString('es-EC')}`;
      case 'done':
        return `Listo · ${job.processed.toLocaleString('es-EC')} filas`;
      case 'empty':
        return 'Sin filas para exportar';
      case 'error':
        return job.error ?? 'No se pudo completar la exportación';
    }
  }
}
